import * as Sentry from '@sentry/nextjs';
import Decimal from 'decimal.js-light';
import { useMemo } from 'react';
import useSWR from 'swr';
import { useAccount } from 'wagmi';
import { ZodError } from 'zod';

import { ApiError } from '@/lib/errors/api';

import { USE_GAS_ORACLE_DEFAULT_CONFIG } from '@/components/TransactionModule/ReviewTransaction/ReviewMarketOrder/utils';

import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';
import { useTradeStore } from '@/store/trade';
import { slippageSelector, useMarketTradeSettingsStore } from '@/store/tradeSettings/market';

import { SwapQuote } from '@/utils/0x.types';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import { getTokenPair } from '@/utils/amplitude';
import { getGasOptionsFromSettings } from '@/utils/gas';
import { routes } from '@/utils/routes';
import { zeroExApiFetcher } from '@/utils/swr';

import { useSwapFeeQuery } from './transactionModule/useSwapFeeQuery';
import useGasOracle from './useGasOracle';
import useGasSettings from './useGasSettings';

interface SwapQuoteParams {
  /** Token to buy */
  buyToken: TokenResult;
  /** Amount of sell token in wei */
  sellAmount?: string;
  /** Token to sell */
  sellToken: TokenResult;
}

/**
 * Fetches the swap `/quote` for a standard market order.
 */
export function useSwapQuote(
  { buyToken, sellAmount, sellToken }: SwapQuoteParams,
  onSuccess: (quote: SwapQuote) => void,
) {
  Sentry.setTag('app.0x_api_version', 2);

  // get parameters
  const chainId = sellToken.chainId;
  const { address: taker } = useAccount();
  const excludedSources = useLiquiditySourcesStore(
    liquiditySourcesEventHandlers,
  ).getExcludedSources(chainId);
  const { slippage } = useMarketTradeSettingsStore(slippageSelector(chainId));
  const { buyInput, isGasless } = useTradeStore((store) => ({
    buyInput: store.buyInput,
    isGasless: store.isCurrentSwapGasless,
  }));
  const {
    gasFeePreset,
    baseFee: customBaseFee,
    priorityFee: customPriorityFee,
    gasPrice: customGasPrice,
  } = useGasSettings({ chainId });
  const { data: fees } = useGasOracle(chainId, USE_GAS_ORACLE_DEFAULT_CONFIG);
  const gasOptions = fees
    ? getGasOptionsFromSettings({
        fees,
        gasFeePreset,
        chainId,
        customGasPrice,
        customBaseFee,
        customPriorityFee,
      })
    : undefined;
  const gasPrice = gasOptions?.type === 'eip1559' ? gasOptions?.maxFeePerGas : gasOptions?.gasPrice;
  const { swapFeeBps, swapFeeRecipient, swapFeeToken } = useSwapFeeQuery({
    buyTokenAddress: buyToken.address,
    buyTokenChainId: buyToken.chainId,
    sellTokenAddress: sellToken.address,
    sellTokenChainId: sellToken.chainId,
  });

  // build URL
  const url = useMemo(() => {
    if (isGasless || !sellAmount || sellAmount === '0' || !taker) return null;

    // Transform slippage to basis points
    const slippageBps = new Decimal(slippage).mul(1e4).toInteger().toString();

    const params = new URLSearchParams({
      chainId: chainId.toString(),
      buyToken: buyToken.address,
      sellToken: sellToken.address,
      sellAmount,
      taker,
      slippageBps,
      ...(swapFeeBps && swapFeeRecipient && swapFeeToken
        ? {
            swapFeeBps: swapFeeBps.toString(),
            swapFeeToken,
            swapFeeRecipient,
          }
        : {}),
    });
    if (gasPrice) params.set('gasPrice', gasPrice.toString());
    if (excludedSources) params.set('excludedSources', excludedSources);

    return routes.api.SWAP_QUOTE + '?' + params.toString();
  }, [
    isGasless,
    sellAmount,
    chainId,
    buyToken.address,
    sellToken.address,
    slippage,
    swapFeeBps,
    swapFeeRecipient,
    swapFeeToken,
    taker,
    gasPrice,
    excludedSources,
  ]);

  // build context for metrics & error reporting
  const context = useMemo(
    () => ({
      buy_token_address: buyToken?.address,
      buy_token_amount: buyInput.decimal,
      buy_token_symbol: buyToken?.symbol,
      chain_id: chainId,
      order_type: 'market_standard',
      pair: getTokenPair(sellToken, buyToken),
      sell_token_address: sellToken?.address,
      sell_token_amount: sellAmount,
      sell_token_symbol: sellToken?.symbol,
      usd_dollar_amount: buyInput.usd,
      wallet_connected_address: taker ?? null,
      swap_fee_bps: swapFeeBps,
      swap_fee_recipient: swapFeeRecipient,
    }),
    [
      buyInput.decimal,
      buyInput.usd,
      buyToken,
      chainId,
      sellAmount,
      sellToken,
      swapFeeBps,
      swapFeeRecipient,
      taker,
    ],
  );

  return useSWR<SwapQuote, ApiError | ZodError | Error>(
    url,
    (url) => zeroExApiFetcher(url, context),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      onSuccess,
    },
  );
}

export class UseSwapQuoteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UseSwapQuoteError';
  }
}
